/**
 * ログインユーザーの利用可能機能 (権限) を保持するコンテキスト (権限管理 SBI-6)。
 *
 * - 認証済みの間だけ描画される想定で、マウント時に `GET /me/permissions` を取得する。
 *   ログアウトでアンマウントされるため、別ユーザーでログインし直しても前の権限は残らない。
 * - 取得中 / 取得失敗の間は **何も許可しない** (安全側)。失敗時は `reload` で再試行できる。
 *
 * ⚠️ ここでの判定は画面の出し分け (利便性) 専用。最終的な拒否は常にバックエンドの 403。
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { PermissionsClient } from '../api/permissions';
import { useAuth } from '../auth/AuthProvider';
import type { AppConfig } from '../config';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';

export type PermissionStatus = 'loading' | 'ready' | 'error';

export interface PermissionState {
  /** 取得状況。`ready` になるまで `has` は常に false */
  status: PermissionStatus;
  /** 利用可能な機能キーの集合 (`ready` 以外では空) */
  features: ReadonlySet<string>;
  /**
   * ログインユーザー自身の `sub` (`ready` 以外では null)。
   * ユーザー管理画面で「自分自身は無効化 / 削除できない」を先に示すために使う (拒否自体はバックエンド)。
   */
  userSub: string | null;
  /** `status === 'error'` のときの表示用メッセージ */
  errorMessage: string | null;
  /** 機能キーを持っているか (画面の出し分け用) */
  has: (featureKey: string) => boolean;
  /** 権限を取得し直す */
  reload: () => void;
}

const EMPTY_FEATURES: ReadonlySet<string> = new Set<string>();

const PermissionContext = createContext<PermissionState | null>(null);

interface PermissionProviderProps {
  config: AppConfig;
  children: React.ReactNode;
}

export function PermissionProvider({
  config,
  children,
}: PermissionProviderProps): React.JSX.Element {
  const { getIdToken } = useAuth();
  const [status, setStatus] = useState<PermissionStatus>('loading');
  const [features, setFeatures] = useState<ReadonlySet<string>>(EMPTY_FEATURES);
  const [userSub, setUserSub] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const client = new PermissionsClient({ getIdToken, config });
    client
      .getMyPermissions()
      .then((permissions) => {
        if (cancelled) return;
        setFeatures(new Set(permissions.features));
        setUserSub(permissions.user_sub);
        setErrorMessage(null);
        setStatus('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // 取得できない場合は何も許可しない (安全側)
        setFeatures(EMPTY_FEATURES);
        setUserSub(null);
        setErrorMessage(describeApiErrorMessage(e, '権限情報の取得に失敗しました'));
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [getIdToken, config, reloadKey]);

  const reload = useCallback(() => {
    setStatus('loading');
    setReloadKey((key) => key + 1);
  }, []);

  const value = useMemo<PermissionState>(
    () => ({
      status,
      features,
      userSub,
      errorMessage,
      has: (featureKey: string) => status === 'ready' && features.has(featureKey),
      reload,
    }),
    [status, features, userSub, errorMessage, reload],
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions(): PermissionState {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return ctx;
}

/**
 * Provider の外では `null` を返す版 (例外にしない)。
 *
 * 権限が無ければ出さないだけの補助的な表示 (履歴画面へのリンクなど) 用。Provider が無い描画では
 * 「権限が無い」と同じ扱い (非表示) にでき、権限に関係ない画面のテストに Provider を要求しない。
 * 権限が必要な画面の判定には `usePermissions` を使う (取り違えると Provider の付け忘れに気付けない)。
 */
export function useOptionalPermissions(): PermissionState | null {
  return useContext(PermissionContext);
}
